import AxiosService from '@/services/api/AxiosService';
import ApiLinks from '@/entities/ApiLinks';
import ApiStatus from '@/entities/enums/apiStatuses';
import { Tier } from '@/entities/tiers/Tier';
import { TierFeature } from '@/entities/tiers/TierFeature';
import { ChangeRequest } from '@/entities/tiers/ChangeRequest';
import { CompanyTier } from '@/entities/tiers/CompanyTier';
import { UsersTier } from '@/entities/tiers/UsersTier';
import { vxm } from '@/store/store.vuex';
import { CustomerPermissions } from '../permissions/CustomerPermissions';
import { AdminPermissions } from '../permissions/AdminPermissions';
import LoggedUserManager from '../LoggedUserManager';
import { FeatureUsage } from '@/entities/tiers/FeatureUsage';
const userCan = LoggedUserManager.checkPermissions;
var vxDepartmentStore = vxm.departmentStore;
export default class TiersRepository {
    constructor() { }
    async getActiveTier(departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.FEATURES_TIERS_READ)) {
            throw 'Forbidden action getActiveTier';
        }
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Tiers.Keys.Active}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new UsersTier();
        else
            return UsersTier.fromApi(response.data.data);
    }
    async getCurrentFeatureUsage(departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.FEATURES_TIERS_MANAGE)) {
            throw 'Forbidden action getCurrentFeatureUsage';
        }
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Tiers.Keys.Usage}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return FeatureUsage.fromApi(response.data.data);
    }
    async getActiveCompanyTier(departmentId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_TIERS_READ)) {
            return null;
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Tiers.Keys.Active}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new CompanyTier();
        else
            return CompanyTier.fromApi(response.data.data);
    }
    async getCompanyTiersHistory(departmentId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_TIERS_HISTORY)) {
            return null;
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Tiers.Keys.Tiers}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return CompanyTier.fromApi(response.data.data);
    }
    async deleteCompanyTier(departmentId, companyTierId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_TIERS_DELETE)) {
            return null;
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Tiers.Keys.Tiers}/${companyTierId}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    async updateCompanyTier(departmentId, companyTierId, dto) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_TIERS_UPDATE)) {
            throw 'Forbidden action updateCompanyTier';
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Tiers.Keys.Tiers}/${companyTierId}`;
        let response = await AxiosService.putDataToServer(url, dto);
        if (response?.status != ApiStatus.SUCCESS)
            return new CompanyTier();
        else
            return CompanyTier.fromApi(response.data.data);
    }
    async getTiers() {
        if (!userCan(AdminPermissions.ADMIN_TIERS_READ)) {
            return [];
        }
        let url = `${ApiLinks.Tiers.AdminHome}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return Tier.fromApi(response.data.data);
    }
    async assignCompanyTier(departmentId, dto) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_TIERS_CREATE)) {
            throw new Error('Forbidden action assignCompanyTier');
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Tiers.Keys.Tiers}`;
        let response = await AxiosService.postDataToServer(url, dto);
        if (response?.status != ApiStatus.SUCCESS)
            throw new Error(response?.data?.errors || `assignCompanyTier failed: ${response?.status}`);
        else
            return CompanyTier.fromApi(response.data.data);
    }
    async activateTierForCompany(departmentId, companyTierId, dto) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_TIERS_ACTIVATE)) {
            throw 'Forbidden action activateTierForCompany';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Tiers.Keys.Tiers}/${companyTierId}${ApiLinks.Tiers.Keys.Activate}`;
        let response = await AxiosService.postDataToServer(url, dto);
        return response?.status === ApiStatus.SUCCESS;
    }
    async deactivateTierForCompany(departmentId, companyTierId, dto) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_TIERS_DEACTIVATE)) {
            throw 'Forbidden action deactivateTierForCompany';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Tiers.Keys.Tiers}/${companyTierId}${ApiLinks.Tiers.Keys.Deactivate}`;
        let response = await AxiosService.postDataToServer(url, dto);
        return response?.status === ApiStatus.SUCCESS;
    }
    async getTier(id) {
        if (!userCan(AdminPermissions.ADMIN_TIERS_READ)) {
            throw 'Forbidden action getTier';
        }
        let url = ApiLinks.AdminTiers.Home + '/' + id;
        let response = await AxiosService.getDataFromServer(url, false);
        if (response?.status != ApiStatus.SUCCESS)
            return response;
        else
            return Tier.fromApi(response.data.data);
    }
    async getAllFeatures() {
        if (!userCan(AdminPermissions.ADMIN_TIERS_READ)) {
            return [];
        }
        let url = ApiLinks.AdminTiers.AllFeatures;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return TierFeature.fromApi(response.data.data);
    }
    async getChangeRequests() {
        if (!userCan(AdminPermissions.ADMIN_TIERS_REQUESTS_READ)) {
            return [];
        }
        let url = ApiLinks.AdminTiers.Requests;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return ChangeRequest.fromApi(response.data.data);
    }
    async createTier(dto) {
        if (!userCan(AdminPermissions.ADMIN_TIERS_CREATE)) {
            throw 'Forbidden action createTier';
        }
        let url = ApiLinks.AdminTiers.Home;
        let response = await AxiosService.postDataToServer(url, dto);
        if (response?.status != ApiStatus.SUCCESS)
            return new Tier();
        else
            return Tier.fromApi(response.data.data);
    }
    async updateTier(dto, id) {
        if (!userCan(AdminPermissions.ADMIN_TIERS_UPDATE)) {
            throw 'Forbidden action updateTier';
        }
        let url = ApiLinks.AdminTiers.Home + '/' + id;
        let response = await AxiosService.putDataToServer(url, dto);
        if (response?.status != ApiStatus.SUCCESS)
            return new Tier();
        else
            return Tier.fromApi(response.data.data);
    }
    getSelectedDepartmentId() {
        return vxDepartmentStore.selectedDepartment.id;
    }
}
